import apiClient, { readSSEStream, getAuthHeader } from './apiClient';

class CampaignService {
  // Get all campaigns
  async getAll(params = {}) {
    try {
      const response = await apiClient.get('/campaigns', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch campaigns',
        data: []
      };
    }
  }

  // Get campaign by ID
  async getById(id, params = {}) {
    try {
      const response = await apiClient.get(`/campaigns/${id}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch campaign',
        data: null
      };
    }
  }

  // Create new campaign
  async create(campaignData) {
    try {
      const response = await apiClient.post('/campaigns', campaignData);
      return response.data;
    } catch (error) {
      console.error('Error creating campaign:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create campaign',
        data: null
      };
    }
  }

  // Presence: ping while editing, leave on unmount
  async presencePing(id) {
    try {
      const response = await apiClient.post(`/campaigns/${id}/presence/ping`);
      return response.data;
    } catch {
      return { success: false, editors: [] };
    }
  }

  async presenceLeave(id) {
    try {
      await apiClient.post(`/campaigns/${id}/presence/leave`);
    } catch { /* silent */ }
  }

  // Update campaign
  async update(id, campaignData) {
    try {
      const response = await apiClient.put(`/campaigns/${id}`, campaignData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        return { success: false, conflict: true, ...error.response.data };
      }
      console.error('Error updating campaign:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update campaign',
        data: null
      };
    }
  }

  // Delete campaign
  async delete(id) {
    try {
      const response = await apiClient.delete(`/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete campaign',
        data: null
      };
    }
  }

  // Update campaign status
  async updateStatus(id, status, previousStatus = null) {
    try {
      const response = await apiClient.patch(`/campaigns/${id}/status`, { 
        status, 
        previousStatus 
      });
      return response.data;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update campaign status',
        data: null
      };
    }
  }

  // Get organizations with departments for targeting
  async getOrganizationsForTargeting() {
    try {
      const response = await apiClient.get('/campaigns/organizations-test');
      console.log('Organization response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch organizations',
        data: []
      };
    }
  }

  // Get status color helper
  getStatusColor(status) {
    const statusColors = {
      'draft': '#faad14',
      'active': '#52c41a',
      'paused': '#1890ff',
      'completed': '#722ed1',
      'cancelled': '#ff4d4f'
    };
    return statusColors[status] || '#8c8c8c';
  }

  // Launch campaign
  async launchCampaign(campaignId) {
    try {
      const response = await apiClient.post(`/campaign-launch/${campaignId}/launch`);
      return response.data;
    } catch (error) {
      console.error('Error launching campaign:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to launch campaign'
      };
    }
  }

  // Get campaign targets
  async getCampaignTargets(campaignId, params = {}) {
    try {
      const response = await apiClient.get(`/campaign-launch/${campaignId}/targets`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign targets:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch campaign targets',
        data: { targets: [], pagination: {} }
      };
    }
  }

  // Get campaign statistics
  async getCampaignStatistics(campaignId, params = {}) {
    try {
      const response = await apiClient.get(`/campaign-launch/${campaignId}/statistics`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign statistics:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch campaign statistics',
        data: { overview: {}, byOrganization: [] }
      };
    }
  }

  // Get campaign question analysis (most difficult and easiest questions)
  async getCampaignQuestionAnalysis(campaignId, params = {}) {
    try {
      const response = await apiClient.get(`/campaign-launch/${campaignId}/question-analysis`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign question analysis:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch question analysis',
        data: { mostDifficult: null, easiest: null, totalResponses: 0 }
      };
    }
  }

  // Generate multi-campaign report from backend (heavy lifting on server)
  async generateMultiCampaignReport(campaignIds, managingGroups = null) {
    try {
      const response = await apiClient.post('/reports/multi-campaign', {
        campaignIds,
        managingGroups
      });
      return response.data;
    } catch (error) {
      console.error('Error generating multi-campaign report:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate multi-campaign report',
        data: null
      };
    }
  }

  // Validate campaign data before creation/update

  // Helper method to get status display
  getStatusDisplay(status) {
    const statusDisplays = {
      'draft': 'Draft',
      'active': 'Active',
      'paused': 'Paused',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return statusDisplays[status] || status;
  }

  // Helper method to format date range
  formatDateRange(startDate, endDate) {
    if (!startDate || !endDate) return 'No date set';
    
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    
    return `${start} - ${end}`;
  }

  // Helper method to get days remaining
  getDaysRemaining(endDate) {
    if (!endDate) return null;
    
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  // Helper method to validate campaign data
  validateCampaignData(data) {
    const errors = [];

    if (!data.name || data.name.trim() === '') {
      errors.push('Campaign name is required');
    }

    if (!data.questionnaire) {
      errors.push('Quiz is required');
    }

    if (!data.startDate) {
      errors.push('Start date is required');
    }

    if (!data.endDate) {
      errors.push('End date is required');
    }

    if (data.startDate && data.endDate && new Date(data.startDate) >= new Date(data.endDate)) {
      errors.push('End date must be after start date');
    }

    // Check for targets - support both legacy and new path-based format,
    // or individually-added people (e.g. via search or CSV import) in place of a department target
    const hasLegacyTargets = data.targets && data.targets.length > 0;
    const hasPathTargets = data.targetPaths &&
      ((data.targetPaths.displayPaths && data.targetPaths.displayPaths.length > 0) ||
       (data.targetPaths.technicalPaths && data.targetPaths.technicalPaths.length > 0));
    const hasAdditionalPeople = data.additionalPeople && data.additionalPeople.length > 0;

    if (!hasLegacyTargets && !hasPathTargets && !hasAdditionalPeople) {
      errors.push('Select at least one target department or add at least one person');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Get campaign target with all related data
  async getCampaignTarget(targetId, params = {}, { fingerprint } = {}) {
    try {
      const headers = fingerprint ? { 'X-Device-Fingerprint': fingerprint } : {};
      // Use longer timeout when loading questions (they may contain large images)
      const includesQuestions = params.include === 'questions' || params.include === 'all';
      const timeout = includesQuestions ? 60000 : 30000; // 60s for questions, 30s for meta
      const response = await apiClient.get(`/campaign-targets/${targetId}`, { params, headers, timeout });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign target:', error);
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      return {
        success: false,
        message: isTimeout 
          ? 'Quiz is taking too long to load. Please check your connection and try again.'
          : (error.response?.data?.message || 'Campaign target not found'),
        data: null
      };
    }
  }

  // Toggle includeInResults for a campaign target
  async toggleTargetIncludeInResults(targetId, includeInResults) {
    try {
      const response = await apiClient.patch(`/campaign-launch/targets/${targetId}/include-in-results`, { includeInResults });
      return response.data;
    } catch (error) {
      console.error('Error toggling includeInResults:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update target'
      };
    }
  }

  // Start campaign quiz (mark as started and update status)
  async startCampaignQuiz(targetId, { fingerprint } = {}) {
    try {
      const body = fingerprint ? { fingerprint } : {};
      const response = await apiClient.post(`/campaign-targets/${targetId}/start`, body);
      return response.data;
    } catch (error) {
      console.error('Error starting campaign quiz:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to start quiz',
        data: null
      };
    }
  }

  // Submit campaign quiz results
  async submitCampaignQuizResults(submissionData) {
    try {
      const response = await apiClient.post('/campaign-targets/submit-results', submissionData);
      return response.data;
    } catch (error) {
      console.error('Error submitting quiz results:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit quiz results',
        data: null
      };
    }
  }

  // Get participant answers for a specific target
  async getParticipantAnswers(targetId) {
    try {
      const response = await apiClient.get(`/campaign-targets/${targetId}/answers`);
      return response.data;
    } catch (error) {
      console.error('Error fetching participant answers:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch participant answers',
        data: null
      };
    }
  }

  // Send WhatsApp invitation to campaign target
  async sendWhatsAppInvitation(targetId) {
    try {
      const response = await apiClient.post('/whatsapp/campaign/target/invite', {
        targetId
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send WhatsApp invitation',
        data: null
      };
    }
  }

  // Send SMS invitation to campaign target
  async sendSMSInvitation(targetId) {
    try {
      const response = await apiClient.post(`/sms/campaign-target/${targetId}`);
      return response.data;
    } catch (error) {
      console.error('Error sending SMS invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send SMS invitation',
        data: null
      };
    }
  }

  // Get leaderboard for a campaign target
  async getLeaderboard(targetId) {
    try {
      const response = await apiClient.get(`/campaign-targets/${targetId}/leaderboard`);
      return response.data;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch leaderboard',
        data: null
      };
    }
  }

  // Stream AI insights for a campaign token by token.
  // callbacks: { onToken(token), onDone(meta), onError(msg) }
  async getCampaignAIInsights(campaignId, callbacks) {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/ai-insights`, {
        headers: { Authorization: getAuthHeader(), Accept: 'text/event-stream' }
      });
      await readSSEStream(response, callbacks);
    } catch (error) {
      console.error('Error getting campaign AI insights:', error);
      callbacks.onError?.(error.message || 'Failed to get AI insights');
    }
  }

  // Stream a follow-up question response token by token.
  // callbacks: { onToken(token), onDone(meta), onError(msg) }
  async askCampaignAIQuestion(campaignId, sessionId, question, callbacks) {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/ai-insights/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader(), Accept: 'text/event-stream' },
        body: JSON.stringify({ sessionId, question })
      });
      if (response.status === 410) {
        callbacks.onError?.('expired');
        return;
      }
      await readSSEStream(response, callbacks);
    } catch (error) {
      console.error('Ask campaign AI question error:', error);
      callbacks.onError?.(error.message || 'Failed to process question');
    }
  }

  // End a campaign AI insights session
  async endCampaignAISession(campaignId, sessionId) {
    try {
      await apiClient.delete(`/campaigns/${campaignId}/ai-insights/session/${sessionId}`, {
        timeout: 10000
      });
    } catch (error) {
      console.warn('End campaign AI session error (non-critical):', error.message);
    }
  }
}

const campaignService = new CampaignService();
export default campaignService;
